import React, { useState, useEffect } from 'react'
import { Box, Button, Grid, Checkbox, FormControlLabel, Alert, Typography, Paper } from '@mui/material'
import { isEqual } from 'lodash'
import { useGlobalNavigationBlock } from './useGlobalNavigationBlock'
import GenericRadioGroup from 'components/UserFields/GenericRadioGroup'
import GenericChipField from 'components/UserFields/GenericChipField'
import GenericMultiSelectCheckboxField from 'components/UserFields/GenericMultiSelectCheckboxField'
import GenericMultipleSelect from 'components/UserFields/GenericMultipleSelect'
import {
  getStatesOptions,
  getAcademicOptions,
  getAreaOptions,
  getGlobalRegionOptions,
  getPracticeTypeOptions,
  AREA_ICONS,
  getRelocationPreferenceTypeOptions,
} from 'components/UserFields/helpers'
import { getChangedFields, clearFieldError } from '../helpers'

const PreferencesSection = ({ formData, onSave }) => {
  const [localFormData, setLocalFormData] = useState({
    ...formData,
  })
  const [initialData, setInitialData] = useState(formData)
  const [hasChanges, setHasChanges] = useState(false)
  const [errors, setErrors] = useState({})
  const [hasLocationPreference, setHasLocationPreference] = useState(
    localFormData.preferredStates?.length > 0,
  )
  useGlobalNavigationBlock(hasChanges)
  const stateOptions = getStatesOptions()

  useEffect(() => {
    setLocalFormData({
      ...formData,
    })
    setInitialData(formData)
    setHasChanges(false)
    setHasLocationPreference(formData.preferredStates?.length > 0)
  }, [formData])

  useEffect(() => {
    setHasChanges(!isEqual(localFormData, initialData))
  }, [localFormData, initialData])

  const handleInputChange = event => {
    const { name, value } = event.target
    clearFieldError(name, setErrors)
    setLocalFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleLocationPreferenceChange = event => {
    setHasLocationPreference(event.target.checked)
    if (!event.target.checked) {
      setLocalFormData(prev => ({ ...prev, preferredStates: [] }))
    }
  }

  const handleSave = () => {
    const changedFields = getChangedFields(localFormData, initialData)

    if (Object.keys(changedFields).length > 0) {
      onSave({
        ...changedFields,
        id: formData.id,
      })
      setInitialData(localFormData)
      setHasChanges(false)
    }
  }

  return (
    <Box>
      {hasChanges && (
        <Alert
          severity='info'
          sx={{
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-icon': {
              color: 'info.main'
            }
          }}
        >
          You have unsaved changes in this section. Please save before navigating away.
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Single consolidated preferences section */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default'
            }}
          >
            {/* Location Preferences */}
            <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 500 }}>
              Location Preferences
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasLocationPreference}
                  onChange={handleLocationPreferenceChange}
                  color='primary'
                />
              }
              label='I have a location preference'
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.9375rem'
                }
              }}
            />
            {hasLocationPreference && (
              <Box sx={{ mt: 2, mb: 4 }}>
                <GenericMultipleSelect
                  label=''
                  name='preferredStates'
                  value={localFormData.preferredStates || []}
                  handleChange={e =>
                    handleInputChange({ target: { name: 'preferredStates', value: e.target.value } })
                  }
                  options={stateOptions}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.paper'
                    }
                  }}
                />
              </Box>
            )}

            {/* Relocation Preference */}
            <Typography variant="subtitle1" sx={{ mb: 3, mt: 4, fontWeight: 500 }}>
              Relocation Preference
            </Typography>
            <Box sx={{ mb: 4 }}>
              <GenericRadioGroup
                value={localFormData.relocationPreference}
                label=''
                name='relocationPreference'
                handleChange={handleInputChange}
                options={getRelocationPreferenceTypeOptions()}
                error={errors.relocationPreference}
                sx={{
                  '& .MuiFormControlLabel-root': {
                    mr: 4
                  }
                }}
              />
            </Box>

            {/* International Interest */}
            <Typography variant="subtitle1" sx={{ mb: 3, mt: 4, fontWeight: 500 }} id="international-interest">
              International Interest
            </Typography>
            <Box sx={{ mb: 4 }}>
              <GenericMultiSelectCheckboxField
                label=''
                name='preferredGlobalRegions'
                value={localFormData.preferredGlobalRegions || []}
                options={getGlobalRegionOptions()}
                handleChange={e =>
                  handleInputChange({
                    target: { name: 'preferredGlobalRegions', value: e.target.value },
                  })
                }
                sx={{
                  '& .MuiFormControlLabel-root': {
                    mr: 4
                  }
                }}
              />
            </Box>

            {/* Area Type Preference */}
            <Typography variant="subtitle1" sx={{ mb: 3, mt: 4, fontWeight: 500 }}>
              Area Type Preference
            </Typography>
            <Box sx={{ mb: 4 }}>
              <GenericChipField
                value={localFormData.preferredAreaTypes}
                label=''
                name='preferredAreaTypes'
                icons={AREA_ICONS}
                options={getAreaOptions()}
                handleChange={e =>
                  handleInputChange({ target: { name: 'preferredAreaTypes', value: e.target.value } })
                }
                sx={{
                  '& .MuiChip-root': {
                    borderRadius: 1.5,
                    height: 32
                  }
                }}
              />
            </Box>

            {/* Practice Type Preference */}
            <Typography variant="subtitle1" sx={{ mb: 3, mt: 4, fontWeight: 500 }}>
              Practice Type Preference
            </Typography>
            <Box sx={{ mb: 4 }}>
              <GenericMultiSelectCheckboxField
                label=''
                name='preferredPracticeTypes'
                value={localFormData.preferredPracticeTypes || []}
                options={getPracticeTypeOptions()}
                handleChange={e =>
                  handleInputChange({
                    target: { name: 'preferredPracticeTypes', value: e.target.value },
                  })
                }
                sx={{
                  '& .MuiFormControlLabel-root': {
                    mr: 4
                  }
                }}
              />
            </Box>

            {/* Academic Setting */}
            <Typography variant="subtitle1" sx={{ mb: 3, mt: 4, fontWeight: 500 }}>
              Academic Setting
            </Typography>
            <Box>
              <GenericRadioGroup
                value={localFormData.academicPreference}
                label=''
                name='academicPreference'
                handleChange={handleInputChange}
                options={getAcademicOptions()}
                sx={{
                  '& .MuiFormControlLabel-root': {
                    mr: 4
                  }
                }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Button
        variant='contained'
        color='primary'
        onClick={handleSave}
        disabled={!hasChanges || Object.keys(errors).length > 0}
        sx={{
          mt: 4,
          px: 4,
          py: 1.5,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: '0.9375rem',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none'
          }
        }}
      >
        Save Changes
      </Button>
    </Box>
  )
}

export default PreferencesSection
